/**
 * CoachRosterDrawer — slide-out detail panel for one coach.
 *
 * Renders permissions, pay editor, copy-from-another-coach, and revoke into
 * a right-side drawer so the roster table stays visible behind it. Closing
 * the drawer doesn't reload the roster; we mutate optimistically.
 */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Trash2, X, DollarSign, History, Copy } from "lucide-react";
import { PERMISSION_LABELS, PERMISSION_GROUPS } from "@/lib/permissions";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PAY_TYPES = [
    { value: "hourly", label: "Hourly", suffix: "/hr" },
    { value: "per_session", label: "Per session", suffix: "/session" },
    { value: "salary", label: "Salary", suffix: "/yr" },
    { value: "stipend", label: "Stipend", suffix: "" },
];

export default function CoachRosterDrawer({ coach, allCoaches, onClose, onChanged }) {
    const isOwner = coach.role === "owner";
    const initialPay = coach.pay || {};
    const [payType, setPayType] = useState(initialPay.pay_type || "hourly");
    const [rateStr, setRateStr] = useState(
        initialPay.rate_cents != null ? String(initialPay.rate_cents / 100) : "",
    );
    const [notes, setNotes] = useState(initialPay.notes || "");
    const [history, setHistory] = useState([]);
    const [historyOpen, setHistoryOpen] = useState(false);
    const [savingPay, setSavingPay] = useState(false);
    const [copyFrom, setCopyFrom] = useState("");

    const updatePermission = async (key, value) => {
        const next = { ...coach.permissions, [key]: value };
        try {
            await axios.patch(
                `${API}/coach/coaches/${coach.user_id}/permissions`,
                { permissions: next },
                { withCredentials: true },
            );
            onChanged({ ...coach, permissions: next });
        } catch (e) {
            toast.error("Couldn't save", { description: e?.response?.data?.detail });
        }
    };

    const applyCopy = async () => {
        if (!copyFrom) return;
        const src = allCoaches.find((c) => c.user_id === copyFrom);
        if (!src || !src.permissions) {
            toast.error("Source coach has no permissions to copy");
            return;
        }
        try {
            await axios.patch(
                `${API}/coach/coaches/${coach.user_id}/permissions`,
                { permissions: src.permissions },
                { withCredentials: true },
            );
            onChanged({ ...coach, permissions: { ...src.permissions } });
            toast.success(`Copied permissions from ${src.name || src.email}`);
            setCopyFrom("");
        } catch (e) {
            toast.error("Couldn't copy", { description: e?.response?.data?.detail });
        }
    };

    const savePay = async (e) => {
        e?.preventDefault?.();
        const cents = rateStr === "" ? null : Math.round(Number(rateStr) * 100);
        if (rateStr !== "" && (Number.isNaN(cents) || cents < 0)) {
            toast.error("Enter a valid rate");
            return;
        }
        setSavingPay(true);
        try {
            const r = await axios.patch(
                `${API}/coach/coaches/${coach.user_id}/pay`,
                { pay_type: payType, rate_cents: cents, notes: notes.trim() || null },
                { withCredentials: true },
            );
            onChanged({ ...coach, pay: r.data.pay });
            toast.success("Pay updated");
        } catch (e) {
            toast.error("Couldn't save pay", { description: e?.response?.data?.detail });
        } finally {
            setSavingPay(false);
        }
    };

    const loadHistory = async () => {
        setHistoryOpen(true);
        try {
            const r = await axios.get(
                `${API}/coach/coaches/${coach.user_id}/pay-history`,
                { withCredentials: true },
            );
            setHistory(r.data?.history || []);
        } catch (e) {
            toast.error("Couldn't load pay history", { description: e?.response?.data?.detail });
        }
    };

    const revoke = async () => {
        if (!window.confirm(`Revoke coach access for ${coach.email}?`)) return;
        try {
            await axios.delete(`${API}/coach/coaches/${coach.user_id}`, { withCredentials: true });
            toast.success("Coach revoked");
            onClose(true);  // signal a full refresh
        } catch (e) {
            toast.error("Couldn't revoke", { description: e?.response?.data?.detail });
        }
    };

    const hardDelete = async () => {
        // Two-step nuke. First confirm explains what's destroyed; second
        // makes them re-type the email so they can't fat-finger it.
        const firstOk = window.confirm(
            `DELETE ${coach.name || coach.email} permanently?\n\n` +
            "This wipes their user record, sessions, magic-link tokens, program assignments, " +
            "subscriptions, packs, and any athletes attached to their account.\n\n" +
            "Activity history they generated (charges, check-ins) stays in the audit log, " +
            "but their name will no longer resolve.\n\n" +
            "If they just left the team, click CANCEL and use \"Revoke coach access\" instead.",
        );
        if (!firstOk) return;
        const typed = window.prompt(
            `Type the coach's email to confirm permanent deletion:\n${coach.email}`,
        );
        if (typed !== coach.email) {
            if (typed != null) toast.error("Email didn't match — cancelled");
            return;
        }
        try {
            await axios.delete(`${API}/coach/users/${coach.user_id}`, { withCredentials: true });
            toast.success("User permanently deleted");
            onClose(true);
        } catch (e) {
            toast.error("Couldn't delete", { description: e?.response?.data?.detail });
        }
    };

    return (
        <>
            {/* Backdrop */}
            <div
                role="presentation"
                onClick={() => onClose(false)}
                className="fixed inset-0 bg-black/60 z-40 backdrop-blur-sm"
                data-testid="coach-drawer-backdrop"
            />
            {/* Drawer */}
            <aside
                data-testid="coach-drawer"
                className="fixed top-0 right-0 h-full w-full sm:w-[480px] bg-zinc-950 border-l border-zinc-800 z-50 overflow-y-auto shadow-2xl"
            >
                {/* Header */}
                <div className="sticky top-0 z-10 bg-zinc-950 border-b border-zinc-900 px-5 py-4 flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                        <span className="inline-flex items-center justify-center w-10 h-10 bg-brand-red text-white font-display text-lg flex-shrink-0">
                            {(coach.name?.[0] || coach.email[0] || "C").toUpperCase()}
                        </span>
                        <div className="min-w-0">
                            <div className="text-sm font-semibold text-white truncate">{coach.name || coach.email}</div>
                            <div className="text-xs text-zinc-400 truncate">{coach.email}</div>
                        </div>
                    </div>
                    <button
                        type="button"
                        data-testid="coach-drawer-close"
                        onClick={() => onClose(false)}
                        className="text-zinc-400 hover:text-white p-1 flex-shrink-0"
                        aria-label="Close drawer"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Body */}
                <div className="px-5 py-5 space-y-6">
                    {/* Permissions */}
                    {isOwner ? (
                        <div className="text-xs text-zinc-300 border border-zinc-800 bg-zinc-900 p-4">
                            Owner has all permissions implicitly.
                        </div>
                    ) : (
                        <div>
                            <div className="flex items-center justify-between mb-3">
                                <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-400">Permissions</div>
                                {allCoaches.filter((c) => c.user_id !== coach.user_id && c.role === "coach").length > 0 && (
                                    <div className="flex items-center gap-1.5">
                                        <select
                                            value={copyFrom}
                                            onChange={(e) => setCopyFrom(e.target.value)}
                                            data-testid="coach-drawer-copyfrom-select"
                                            className="bg-black border border-zinc-800 px-2 py-1 text-xs text-white focus:outline-none focus:border-brand-red max-w-[140px]"
                                        >
                                            <option value="">Copy from…</option>
                                            {allCoaches
                                                .filter((c) => c.user_id !== coach.user_id && c.role === "coach" && c.permissions)
                                                .map((c) => (
                                                    <option key={c.user_id} value={c.user_id}>{c.name || c.email}</option>
                                                ))
                                            }
                                        </select>
                                        <button
                                            type="button"
                                            disabled={!copyFrom}
                                            onClick={applyCopy}
                                            data-testid="coach-drawer-copyfrom-apply"
                                            className="text-[10px] tracking-[0.2em] uppercase text-brand-red hover:text-white disabled:opacity-30 inline-flex items-center gap-1"
                                        >
                                            <Copy className="w-3 h-3" /> Apply
                                        </button>
                                    </div>
                                )}
                            </div>
                            <div className="space-y-4">
                                {PERMISSION_GROUPS.map((group) => (
                                    <div key={group.key}>
                                        <div className="text-[9px] tracking-[0.25em] uppercase text-zinc-500 mb-1.5">{group.label}</div>
                                        <div className="grid grid-cols-1 gap-2">
                                            {group.keys.map((key) => (
                                                <PermissionToggle
                                                    key={key}
                                                    permKey={key}
                                                    enabled={!!coach.permissions?.[key]}
                                                    onChange={(v) => updatePermission(key, v)}
                                                    testId={`coach-perm-${coach.user_id}-${key}`}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Pay */}
                    {!isOwner && (
                        <div>
                            <div className="text-[10px] tracking-[0.25em] uppercase text-emerald-300 mb-3 inline-flex items-center gap-2">
                                <DollarSign className="w-3.5 h-3.5" /> Pay
                            </div>
                            <form onSubmit={savePay} data-testid={`coach-pay-${coach.user_id}`} className="space-y-3">
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="text-[10px] tracking-[0.25em] uppercase text-zinc-400 block mb-1">Type</label>
                                        <select
                                            value={payType}
                                            onChange={(e) => setPayType(e.target.value)}
                                            data-testid={`coach-pay-type-${coach.user_id}`}
                                            className="w-full bg-black border border-zinc-800 px-3 py-2 text-sm text-white focus:outline-none focus:border-emerald-500"
                                        >
                                            {PAY_TYPES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="text-[10px] tracking-[0.25em] uppercase text-zinc-400 block mb-1">Rate ($)</label>
                                        <input
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            value={rateStr}
                                            onChange={(e) => setRateStr(e.target.value)}
                                            placeholder="45.00"
                                            data-testid={`coach-pay-rate-${coach.user_id}`}
                                            className="w-full bg-black border border-zinc-800 px-3 py-2 text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:border-emerald-500"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="text-[10px] tracking-[0.25em] uppercase text-zinc-400 block mb-1">Notes</label>
                                    <input
                                        type="text"
                                        value={notes}
                                        onChange={(e) => setNotes(e.target.value)}
                                        placeholder="Effective 1/15, raises 7/1"
                                        data-testid={`coach-pay-notes-${coach.user_id}`}
                                        className="w-full bg-black border border-zinc-800 px-3 py-2 text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:border-emerald-500"
                                    />
                                </div>
                                <div className="flex items-center gap-2">
                                    <button
                                        type="submit"
                                        disabled={savingPay}
                                        data-testid={`coach-pay-save-${coach.user_id}`}
                                        className="btn-tactical text-xs inline-flex items-center gap-1.5 disabled:opacity-50"
                                    >
                                        {savingPay ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                                        Save pay
                                    </button>
                                    <button
                                        type="button"
                                        onClick={loadHistory}
                                        data-testid={`coach-pay-history-${coach.user_id}`}
                                        className="btn-ghost-tactical text-xs inline-flex items-center gap-1.5"
                                    >
                                        <History className="w-3.5 h-3.5" /> History
                                    </button>
                                </div>
                                {historyOpen && (
                                    <div data-testid={`coach-pay-history-list-${coach.user_id}`} className="border border-zinc-800 bg-black p-3 mt-3 rounded">
                                        <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 mb-2">Pay history</div>
                                        {history.length === 0 ? (
                                            <div className="text-xs text-zinc-500 italic">No history yet.</div>
                                        ) : (
                                            <ul className="text-xs text-zinc-300 space-y-1">
                                                {history.map((h, i) => {
                                                    const suffix = PAY_TYPES.find((p) => p.value === h.pay_type)?.suffix || "";
                                                    return (
                                                        <li key={i} className="flex gap-3">
                                                            <span className="text-zinc-500 w-24 flex-shrink-0">
                                                                {new Date(h.updated_at).toLocaleDateString()}
                                                            </span>
                                                            <span className="text-white font-mono">
                                                                {h.rate_cents != null ? `$${(h.rate_cents / 100).toFixed(2)}${suffix}` : "—"}
                                                            </span>
                                                            <span className="text-zinc-500 truncate flex-1">{h.notes || ""}</span>
                                                        </li>
                                                    );
                                                })}
                                            </ul>
                                        )}
                                    </div>
                                )}
                            </form>
                        </div>
                    )}

                    {/* Revoke + Hard delete */}
                    {!isOwner && (
                        <div className="pt-4 border-t border-zinc-900 space-y-3">
                            <div>
                                <button
                                    type="button"
                                    data-testid={`coach-revoke-${coach.user_id}`}
                                    onClick={revoke}
                                    className="text-zinc-300 hover:text-amber-300 text-[10px] tracking-[0.25em] uppercase inline-flex items-center gap-2"
                                >
                                    <Trash2 className="w-3.5 h-3.5" /> Revoke coach access
                                </button>
                                <p className="text-[10px] text-zinc-500 mt-1 leading-snug">
                                    Demotes them to a regular client. Keeps their user record + history. Use this when a coach leaves the team.
                                </p>
                            </div>
                            <div>
                                <button
                                    type="button"
                                    data-testid={`coach-delete-${coach.user_id}`}
                                    onClick={hardDelete}
                                    className="text-red-400 hover:text-red-300 text-[10px] tracking-[0.25em] uppercase inline-flex items-center gap-2"
                                >
                                    <Trash2 className="w-3.5 h-3.5" /> Delete user entirely
                                </button>
                                <p className="text-[10px] text-zinc-500 mt-1 leading-snug">
                                    Wipes the user record + sessions + assignments + subscriptions + their athletes. Use only if they had no real client history. Requires email confirmation.
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </aside>
        </>
    );
}

export function PermissionToggle({ permKey, enabled, onChange, testId }) {
    const meta = PERMISSION_LABELS[permKey];
    return (
        <button
            type="button"
            data-testid={testId}
            onClick={() => onChange(!enabled)}
            className={`text-left p-2.5 border transition-colors ${
                enabled ? "border-brand-red bg-brand-red/5" : "border-zinc-900 hover:border-zinc-700"
            }`}
        >
            <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                    <div className="text-sm font-semibold text-white">{meta.label}</div>
                    <div className="text-[11px] text-zinc-400 mt-0.5 leading-tight">{meta.desc}</div>
                </div>
                <div className={`inline-flex w-9 h-5 rounded-full p-0.5 transition-colors flex-shrink-0 ${
                    enabled ? "bg-brand-red" : "bg-zinc-800"
                }`}>
                    <span className={`inline-block w-4 h-4 bg-white rounded-full transform transition-transform ${
                        enabled ? "translate-x-4" : ""
                    }`} />
                </div>
            </div>
        </button>
    );
}
